import { useMsal } from '@azure/msal-react';
import { Stack } from '@fluentui/react';
import { RootState } from 'app/store';
import { IGridItem } from 'components/controls/CustomDetailList/IGridItem';
import LookupControlDialog, { TSearchPredicateAsync } from 'components/controls/LookupControlDialog';
import { fadeIn } from 'global/Application';
import { AppDataSource } from 'global/DataSources/AppDataSource';
import { MsalTokenSource } from 'global/DataSources/MsalTokenSource';
import { addUpdateBenefit, setAssociationTemplateSnapshot } from 'global/Reducer/dataSlice';
import { IExtendedColumn } from 'model/IExtendedColumn';
import {
    IBenefit as FAM_IBenefit,
    IBenefitRef as FAM_IBenefitRef,
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IObjectWithNameId as FAM_IObjectWithNameId,
    IPlan as FAM_IPlan
} from 'model/FlexibleAttributeModel';
import { useEffect, useState } from 'react';
import { connect, ConnectedProps, useDispatch, useSelector } from 'react-redux';
import SnapshotLookup from './Controls/SnapshotLookup';
import { benefitNamesMatch, categoriesMatch, planNamesMatch } from './associationTemplateUtils';

interface IProps extends TComponentConnectedProps {
    isOpen: boolean;
    benefit: FAM_IBenefit | undefined;
    onDismiss: () => void;
    onSuccess?: (message: string) => void;
    onError?: (message: string) => void;
}

interface IBenefitSearchResult extends FAM_IObjectWithNameId {
    benefit: FAM_IBenefit;
}

const AssociationTemplateApplyPanel = (props: IProps) => {
    const { isOpen, benefit, onDismiss, onSuccess, onError } = props;

    const dispatch = useDispatch();
    const { instance } = useMsal();
    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const associationTemplateSnapshot = useSelector((state: RootState) => state.data.associationTemplateSnapshot);

    const currentBenefits = (props.state.data.json?.benefits || []).filter((b: FAM_IBenefit) => !b.deactivated);

    // Step 1 — snapshot selection
    const [selectedSnapshot, setSelectedSnapshot] = useState<FAM_IEntityReferenceValue | null>(null);
    const [loadedBenefits, setLoadedBenefits] = useState<FAM_IBenefit[]>([]);
    const [loadedSnapshotId, setLoadedSnapshotId] = useState<string | null>(null);
    // Stores template benefit ID → plan names; used only for pick-time plan validation.
    const [templateBenefitPlans, setTemplateBenefitPlans] = useState<Map<string, string[]>>(new Map());

    // Reset when panel opens/closes
    useEffect(() => {
        if (!isOpen) return;
        setSelectedSnapshot(null);
        setLoadedBenefits([]);
        setLoadedSnapshotId(null);
        setTemplateBenefitPlans(new Map());
    }, [isOpen]);

    // When snapshot is picked, load it and filter benefits by category
    useEffect(() => {
        if (!selectedSnapshot) return;

        const load = async () => {
            try {
                let benefits: FAM_IBenefit[] | undefined;
                let templatePlans: FAM_IPlan[] | undefined;

                const cachedId = associationTemplateSnapshot?.snapshot?.bcbs_groupconfigurationsnapshotid;
                if (cachedId === selectedSnapshot.id && associationTemplateSnapshot?.json?.benefits) {
                    benefits = associationTemplateSnapshot.json.benefits;
                    templatePlans = associationTemplateSnapshot.json.plans;
                } else {
                    const snapshotRecord = await dataSource.getGroupConfigurationSnapshot(selectedSnapshot.id);
                    if (!snapshotRecord) return;

                    const downloadResult = await dataSource.downloadGroupConfigurationSnapshot(snapshotRecord);
                    if (!downloadResult?.json) return;

                    const account = snapshotRecord._bcbs_groupid_value
                        ? await dataSource.getAccount(snapshotRecord._bcbs_groupid_value)
                        : null;

                    dispatch(setAssociationTemplateSnapshot({
                        account: account ?? {} as any,
                        snapshot: snapshotRecord,
                        json: downloadResult.json,
                        config: {} as FAM_IGroupConfigurationConfig
                    }));

                    benefits = downloadResult.json.benefits;
                    templatePlans = downloadResult.json.plans;
                }

                // Step 1 — Category filter.
                // Match by exact category GUID. If no template benefit shares the GUID
                // (can happen when configs come from different environments), fall back
                // to all active benefits so the user can still search.
                const sourceCategoryId = benefit?.bcbs_config_categoryid
                    ? String(benefit.bcbs_config_categoryid).toLowerCase()
                    : null;

                const activeBenefits = (benefits ?? []).filter((b: FAM_IBenefit) => !b.deactivated);

                const sameCategoryPool = sourceCategoryId !== null
                    ? activeBenefits.filter(b =>
                        String(b.bcbs_config_categoryid ?? '').toLowerCase() === sourceCategoryId)
                    : activeBenefits;
                const pool = sameCategoryPool.length > 0 ? sameCategoryPool : activeBenefits;

                // Step 2 — Benefit name match within the pool.
                // If any benefit in the pool matches by name (token-subset), show only those.
                // If nothing matches by name, show the entire same-category pool so the
                // user can still pick manually.
                const sourceBenefitName = benefit?.name ?? '';
                const nameMatched = sourceBenefitName
                    ? pool.filter(b => benefitNamesMatch(sourceBenefitName, b.name ?? ''))
                    : [];
                const filtered = nameMatched.length > 0 ? nameMatched : pool;

                // Step 3 — Build plan lookup for pick-time validation only.
                // Plans are NOT used to filter the display list. They are only used in
                // handlePick to reject cross-plan assignments.
                const planMap = new Map<string, string[]>();
                (templatePlans ?? []).filter((p: FAM_IPlan) => !p.deactivated).forEach((p: FAM_IPlan) => {
                    p.benefitRefs?.forEach((r: FAM_IBenefitRef) => {
                        const key = (r.id ?? '').toLowerCase();
                        if (!planMap.has(key)) planMap.set(key, []);
                        planMap.get(key)!.push(p.name);
                    });
                });
                setTemplateBenefitPlans(planMap);

                setLoadedBenefits(filtered);
                setLoadedSnapshotId(selectedSnapshot.id);
            } catch (err: any) {
                console.error('Error loading snapshot for Association Template:', err);
            }
        };

        void load();
    }, [selectedSnapshot]);

    // -------------------------------------------------------
    // LookupControlDialog search predicate
    // -------------------------------------------------------
    const onSearchBenefits: TSearchPredicateAsync<IBenefitSearchResult> = async (value) => {
        const search = (value ?? '').toLowerCase();

        const items: IGridItem<IBenefitSearchResult>[] = loadedBenefits
            .filter(b => {
                // Exclude if already linked by template ID, OR if the current benefit was
                // imported from a template and matches by category + name.
                // Manually-added benefits ("M") with the same name are NOT excluded — the
                // user still needs to be able to apply a template to them.
                const alreadyUsed = currentBenefits.some((cb: FAM_IBenefit) => {
                    const byId = cb.bcbs_associationtemplatebenefitid === b.id;
                    const isFromTemplate = cb.bcbs_fromassociationtemplate === true
                        || !!cb.bcbs_associationtemplatebenefitid;
                    const catCb = String(cb.bcbs_config_categoryid ?? '').trim();
                    const catB = String(b.bcbs_config_categoryid ?? '').trim();
                    const sameCategory = catCb !== '' && catB !== '' && catCb === catB;
                    const sameName = cb.name === b.name;
                    return byId || (isFromTemplate && sameCategory && sameName);
                });
                if (alreadyUsed) return false;

                return !search ||
                    b.name?.toLowerCase().includes(search) ||
                    b.bcbs_name?.toLowerCase().includes(search);
            })
            .map((b, idx) => ({
                key: idx,
                _index: idx,
                name: b.name ?? '',
                scope: b.bcbs_name ?? '',
                data: {
                    id: b.id,
                    name: b.name ?? '',
                    benefit: b,
                },
            }));

        const columns: IExtendedColumn[] = [
            {
                key: 'name',
                name: 'Benefit Name',
                fieldName: 'name',
                minWidth: 250,
                maxWidth: 400,
                isResizable: true,
            },
            {
                key: 'bcbs_name',
                name: 'Internal Name',
                fieldName: 'scope',
                minWidth: 180,
                maxWidth: 300,
                isResizable: true,
            },
        ];

        return {
            items,
            columns,
            showNoResultPrompt: items.length === 0,
            showLimitedResultPrompt: false,
        };
    };

    // -------------------------------------------------------
    // Apply picked benefit
    // -------------------------------------------------------
    const handlePick = (picks: IBenefitSearchResult[] | null) => {
        if (!benefit || !picks || picks.length === 0) return;

        try {
            const selectedBenefit = picks[0].benefit;

            // Plan validation: if the source benefit belongs to plans, the picked template
            // benefit must belong to at least one compatible plan.
            // Example: source in "Plan 11" → template benefit in "Plan 1" is rejected.
            const sourceBenefitId = (benefit.id ?? '').toLowerCase();
            const sourcePlanNames = (props.state.data.json?.plans ?? [])
                .filter((p: FAM_IPlan) =>
                    !p.deactivated &&
                    p.benefitRefs?.some((r: FAM_IBenefitRef) => (r.id ?? '').toLowerCase() === sourceBenefitId)
                )
                .map((p: FAM_IPlan) => p.name);

            if (sourcePlanNames.length > 0) {
                const tplPlanNames = templateBenefitPlans.get((selectedBenefit.id ?? '').toLowerCase()) ?? [];
                if (tplPlanNames.length > 0) {
                    const compatible = tplPlanNames.some(tplName =>
                        sourcePlanNames.some(srcName => planNamesMatch(srcName, tplName))
                    );
                    if (!compatible) {
                        onError?.(
                            `Cannot apply: "${selectedBenefit.name}" belongs to plan "${tplPlanNames.join('", "')}" ` +
                            `which does not match the current benefit's plan "${sourcePlanNames.join('", "')}". ` +
                            `Please select a benefit from a compatible plan.`
                        );
                        return;
                    }
                }
            }

            const cloned: FAM_IBenefit = JSON.parse(JSON.stringify(benefit));

            cloned.bcbs_name = selectedBenefit.bcbs_name;
            cloned.fields = JSON.parse(JSON.stringify(selectedBenefit.fields));
            cloned.tiers = JSON.parse(JSON.stringify(selectedBenefit.tiers));

            cloned.bcbs_fromassociationtemplate = true;
            cloned.bcbs_associationtemplatebenefitid = selectedBenefit.id;
            cloned.bcbs_associationtemplatesnapshotid =
                associationTemplateSnapshot?.snapshot?.bcbs_groupconfigurationsnapshotid
                ?? selectedSnapshot?.id
                ?? null;
            cloned.bcbs_associationtemplateaction = 'Sync';

            dispatch(addUpdateBenefit([cloned]));

            onSuccess?.(`Benefit successfully updated from Association Template: "${selectedBenefit.name}".`);
            onDismiss();
        } catch (err: any) {
            console.error('Error applying Association Template benefit:', err);
            onError?.(err?.message || 'Failed to apply Association Template benefit.');
            onDismiss();
        }
    };

    // -------------------------------------------------------
    // Render
    // -------------------------------------------------------
    function onRenderBody(): JSX.Element {
        return (
            <div className={fadeIn}>
                <Stack tokens={{ childrenGap: 10 }}>

                    <SnapshotLookup
                        defaultValueAccount={{ id: '09fb458d-49d8-eb11-bacb-000d3a533755', name: 'Association Template', type: 'entity', entityType: 'account' }}
                        defaultValueViewKey="associationTemplateView"
                        searchOnMount={true}
                        onPick={(snapshotRef) => {
                            setSelectedSnapshot(snapshotRef);
                        }}
                        onCancel={onDismiss}
                        label="Snapshot"
                    />

                    <LookupControlDialog
                        isOpen={
                            isOpen &&
                            selectedSnapshot !== null &&
                            loadedSnapshotId === selectedSnapshot?.id
                        }
                        label="Select Benefit from Template"
                        variant="single"
                        onSearch={onSearchBenefits}
                        onPick={(picks) => {
                            handlePick(picks);
                        }}
                        onCancel={() => {
                            setSelectedSnapshot(null);
                            setLoadedBenefits([]);
                            setLoadedSnapshotId(null);
                            onDismiss();
                        }}
                        onDismiss={() => {
                            setSelectedSnapshot(null);
                            setLoadedBenefits([]);
                            setLoadedSnapshotId(null);
                            onDismiss();
                        }}
                    />

                </Stack>
            </div>
        );
    }

    return onRenderBody();
};

function mapStateToProps(state: RootState) { return { state }; }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(AssociationTemplateApplyPanel);
